import { useEffect, useState } from "react";
import { geoServerBaseUrl, geoServerLegendJsonParams } from "../services/geoServer";
import { geoServerAuthHeaders } from "../services/geoServerAuth";

export interface RasterLegendStop {
  offsetPct: number;
  color: string;
}

export interface RasterLegend {
  min: number;
  max: number;
  minLabel: string;
  maxLabel: string;
  stops: RasterLegendStop[];
}

interface ActiveRasterRef {
  workspace: string;
  layerName: string;
}

const cache = new Map<string, RasterLegend | null>();

// The MDT (relevo) coverage has no colormap in its SLD on GeoServer — it's
// styled as generic opaque grayscale (single "raster" style, confirmed via
// GetCapabilities), so GetLegendGraphic never returns a ramp for it. Its
// GeoServer REST API requires auth we don't have, so min/max here come from
// `gdalinfo -stats` on the source COG (pe3d/MOSAICO_100m_COG.tif) instead —
// real elevation range in meters, not a guess. Colors are a placeholder
// hypsometric tint (green→yellow→orange→brown) since no server ramp exists
// to reuse.
const MDT_FALLBACK_LEGEND: RasterLegend = {
  min: -16.57,
  max: 1195.41,
  minLabel: "-16,6",
  maxLabel: "1195,4",
  stops: [
    { offsetPct: 0, color: "#1a9850" },
    { offsetPct: 25, color: "#91cf60" },
    { offsetPct: 50, color: "#fee08b" },
    { offsetPct: 75, color: "#fc8d59" },
    { offsetPct: 100, color: "#b35806" },
  ],
};

const FALLBACK_LEGENDS: Record<string, RasterLegend> = {
  "pepe-raster:MOSAICO_100m_COG": MDT_FALLBACK_LEGEND,
  // MOSAICO_otimizado é a mesma cobertura (relevo/MDT) servida em resolução
  // detalhada acima do limiar de zoom (ver `detailedLayerName` em layers.ts).
  // GetLegendGraphic também não retorna colormap para ela, e o terreno é o
  // mesmo, então reusa o mesmo fallback — senão a legenda some ao aproximar
  // o zoom.
  "pepe-raster:MOSAICO_otimizado": MDT_FALLBACK_LEGEND,
};

function parseColormapEntries(data: any): RasterLegend | null {
  const colormap =
    data?.Legend?.[0]?.rules?.[0]?.symbolizers?.[0]?.Raster?.colormap;

  if (!colormap || colormap.type !== "ramp" || !Array.isArray(colormap.entries)) {
    return null;
  }

  const entries = colormap.entries
    .map((e: any) => ({
      quantity: Number(e.quantity),
      color: e.color as string,
      label: e.label as string,
    }))
    .filter((e: any) => Number.isFinite(e.quantity))
    .sort((a: any, b: any) => a.quantity - b.quantity);

  if (entries.length < 2) return null;

  const min = entries[0].quantity;
  const max = entries[entries.length - 1].quantity;
  const range = max - min;
  if (!(range > 0)) return null;

  return {
    min,
    max,
    minLabel: entries[0].label,
    maxLabel: entries[entries.length - 1].label,
    stops: entries.map((e: any) => ({
      offsetPct: ((e.quantity - min) / range) * 100,
      color: e.color,
    })),
  };
}

/**
 * Fetches the real color ramp (min/max/stops) used by a raster's SLD style,
 * via GeoServer's GetLegendGraphic in JSON format. Returns null for layers
 * without a continuous "ramp" colormap (e.g. binary suitability masks, or
 * styles with no colormap at all), which have no meaningful pixel gradient.
 */
export function useRasterLegend(activeRaster: ActiveRasterRef | null): RasterLegend | null {
  const [legend, setLegend] = useState<RasterLegend | null>(null);

  useEffect(() => {
    if (!activeRaster) {
      setLegend(null);
      return;
    }

    const { workspace, layerName } = activeRaster;
    const cacheKey = `${workspace}:${layerName}`;

    if (cache.has(cacheKey)) {
      setLegend(cache.get(cacheKey) ?? null);
      return;
    }

    let cancelled = false;
    setLegend(null);

    const params = new URLSearchParams({
      ...geoServerLegendJsonParams,
      layer: layerName,
    });

    fetch(`${geoServerBaseUrl}/${workspace}/wms?${params}`, {
      headers: geoServerAuthHeaders(workspace),
    })
      .then((res) => res.json())
      .then((data) => {
        const parsed = parseColormapEntries(data) ?? FALLBACK_LEGENDS[cacheKey] ?? null;
        cache.set(cacheKey, parsed);
        if (!cancelled) setLegend(parsed);
      })
      .catch(() => {
        // A transient network/parse failure must not be cached: doing so would
        // suppress the legend until a full page reload. Only the fallback (a
        // real, static answer) is worth keeping.
        const fallback = FALLBACK_LEGENDS[cacheKey] ?? null;
        if (fallback) cache.set(cacheKey, fallback);
        if (!cancelled) setLegend(fallback);
      });

    return () => {
      cancelled = true;
    };
  }, [activeRaster?.workspace, activeRaster?.layerName]);

  return legend;
}
